<div class="printing-edit">
  <div class="printing-edit__content">
    <div class="printing-edit__layout">
      <div class="printing-edit__header">
        <div class="printing-edit__heading">
          <h1 class="page-title">Edit Printing</h1>
          <p class="page-subtitle">Draft can be edited. Inspection will increase stock only on accepted quantity.</p>
        </div>
        <div class="printing-edit__actions">
          <app-button type="button" variant="secondary" (click)="goBack()">Back</app-button>
          @if (isReady && isDraft) {
          <app-button type="button" [disabled]="isSaving || isAttachmentSaving || form.invalid || !printingId" (click)="save()"
            >{{ isSaving ? "Saving..." : "Save" }}</app-button
          >
          } @if (isReady && isShipped) {
          <app-button type="button" [disabled]="isSaving" (click)="inspect()"
            >{{ isSaving ? "Inspecting..." : "Inspect" }}</app-button
          >
          }
        </div>
      </div>

      @if (isReady && printing) {
      <app-card>
        <form [formGroup]="form" class="printing-edit__form">
          <div class="printing-edit__meta-grid">
            <div class="printing-edit__form-field">
              <label for="printing-pages-edit-printingeditpage-field-1" class="printing-edit__form-label">Printing No</label>
              <input type="text" class="printing-edit__form-input" [value]="printing.printingNo" disabled />
            </div>

            <div class="printing-edit__form-field">
              <label for="printing-pages-edit-printingeditpage-field-2" class="printing-edit__form-label">Status</label>
              <input type="text" class="printing-edit__form-input" [value]="printing.status" disabled />
            </div>
          </div>

          <div class="printing-edit__form-grid">
            <div class="printing-edit__form-field">
              <label for="printing-pages-edit-printingeditpage-field-3" class="printing-edit__form-label">Vendor</label>
              <app-autocomplete
                [prefetchOnInit]="true"
                [options]="vendorOptions"
                [selectedValue]="selectedVendorId"
                [disabled]="!isDraft"
                [minLength]="1"
                [debounceMs]="300"
                placeholder="Search printing vendor..."
                (searchChange)="searchVendors($event)"
                (selectionChange)="onVendorChange($event)"></app-autocomplete>
            </div>

            <div class="printing-edit__form-field">
              <label for="printing-pages-edit-printingeditpage-field-4" class="printing-edit__form-label">Warehouse</label>
              <app-autocomplete
                [prefetchOnInit]="true"
                [options]="warehouseOptions"
                [selectedValue]="selectedWarehouseId"
                [disabled]="!isDraft"
                [minLength]="1"
                [debounceMs]="300"
                placeholder="Search warehouse..."
                (searchChange)="searchWarehouses($event)"
                (selectionChange)="onWarehouseChange($event)"></app-autocomplete>
            </div>
          </div>

          <div class="printing-edit__form-field">
            <label for="printing-pages-edit-printingeditpage-field-5" class="printing-edit__form-label">Note</label>
            <app-rich-text-editor formControlName="note" placeholder="Write printing note..."></app-rich-text-editor>
          </div>
        </form>
      </app-card>

      <app-card>
        <div class="printing-edit__attachments" [formGroup]="form">
          <div class="printing-edit__section-header">
            <h2 class="printing-edit__section-title">Attachments</h2>
          </div>

          <app-file-uploader
            formControlName="attachments"
            [folder]="attachmentFolder"
            [multiple]="true"
            viewMode="thumbnail"
            [disabled]="isAttachmentUploaderDisabled()"
            label="Upload printing attachments"
            [hint]="getAttachmentHint()"
            emptyStateTitle="No attachments added"
            (filesChange)="onAttachmentsChange($event)"
            emptyStateDescription="Uploaded printing attachments will appear here."></app-file-uploader>
        </div>
      </app-card>

      @if (isDraft) {
      <app-card>
        <div [formGroup]="form">
          <div class="printing-edit__section-header">
            <h2 class="printing-edit__section-title">Printing Items</h2>
            <app-button
              type="button"
              variant="secondary"
              [disabled]="!isDraft"
              (click)="addDraftItem()"
              title="Add item"
              aria-label="Add item">
              <ng-glyph name="add" size="18"></ng-glyph>
            </app-button>
          </div>

          <div formArrayName="items" class="printing-edit__items">
            @for (itemControl of items.controls; track $index) {
            <div [formGroupName]="$index" class="printing-edit__item-row">
              <div class="printing-edit__item-field">
                <label for="printing-pages-edit-printingeditpage-field-6" class="printing-edit__form-label">Material</label>
                <app-autocomplete
                  [prefetchOnInit]="true"
                  [options]="materialOptionsByIndex[$index]"
                  [selectedValue]="selectedMaterialIds[$index]"
                  [minLength]="1"
                  [debounceMs]="300"
                  placeholder="Search material..."
                  (searchChange)="searchMaterials($event)"
                  (selectionChange)="onMaterialChange($index, $event)"></app-autocomplete>
              </div>

              <div class="printing-edit__item-field">
                <label for="printing-pages-edit-printingeditpage-field-7" class="printing-edit__form-label">Planned Qty</label>
                <input
                  type="number"
                  min="1"
                  appQtyInput
                  class="printing-edit__form-input printing-edit__item-qty-input"
                  formControlName="plannedQty" />
              </div>

              <div class="printing-edit__item-action">
                <app-button
                  type="button"
                  variant="danger"
                  [disabled]="!isDraft || items.length <= 1"
                  (click)="removeDraftItem($index)"
                  title="Remove item"
                  aria-label="Remove item">
                  <ng-glyph name="delete" size="18"></ng-glyph>
                </app-button>
              </div>
            </div>
            }
          </div>
        </div>
      </app-card>
      } @if (isShipped || isInspected) {
      <app-card>
        <div [formGroup]="form">
          <div class="printing-edit__section-header">
            <h2 class="printing-edit__section-title">Inspection</h2>
          </div>

          <div formArrayName="inspectItems" class="printing-edit__inspect-list">
            @for (inspectControl of inspectItems.controls; track $index) {
            <div [formGroupName]="$index" class="printing-edit__inspect-row">
              <div class="printing-edit__inspect-field">
                <label for="printing-pages-edit-printingeditpage-field-8" class="printing-edit__form-label">Material</label>
                <input type="text" class="printing-edit__form-input" [value]="getInspectionMaterialLabel($index)" disabled />
              </div>
              <div class="printing-edit__inspect-field">
                <label for="printing-pages-edit-printingeditpage-field-9" class="printing-edit__form-label">Planned Qty</label>
                <input type="number" min="0" class="printing-edit__form-input" [value]="getInspectionPlannedQty($index)" disabled />
              </div>
              <div class="printing-edit__inspect-field">
                <label for="printing-pages-edit-printingeditpage-field-10" class="printing-edit__form-label">Received Qty</label>
                <input type="number" min="0" appQtyInput class="printing-edit__form-input" formControlName="receivedQty" />
              </div>
              <div class="printing-edit__inspect-field">
                <label for="printing-pages-edit-printingeditpage-field-11" class="printing-edit__form-label">Accepted Qty</label>
                <input type="number" min="0" appQtyInput class="printing-edit__form-input" formControlName="acceptedQty" />
              </div>
              <div class="printing-edit__inspect-field">
                <label for="printing-pages-edit-printingeditpage-field-12" class="printing-edit__form-label">Rejected Qty</label>
                <input type="number" min="0" appQtyInput class="printing-edit__form-input" formControlName="rejectedQty" />
              </div>
              <div class="printing-edit__inspect-field printing-edit__inspect-field--full">
                <label for="printing-pages-edit-printingeditpage-field-13" class="printing-edit__form-label">Inspection Note</label>
                <app-rich-text-editor formControlName="inspectionNote" placeholder="Write inspection note..."></app-rich-text-editor>
              </div>
            </div>
            }
          </div>
        </div>
      </app-card>
      } }
    </div>
  </div>
</div>
